import { Button } from "@workspace/web-ui/components/button";
import { Image } from "@workspace/web-ui/components/image";
import { useState, useEffect } from "react";

import { ConsultationModal } from "../drawers/consultation-modal";
import { FormattedEncounter } from "../../../encounters/types";

import { formatTimeAgo } from "../../../encounters/components/urgentcare-card";
import { ConsultationStatus } from "../consultation-status";
import { getRouteApi } from "@tanstack/react-router";
import { CircleCheck, Clock, Info } from "lucide-react";

interface InfoRowProps {
  icon: string;
  alt: string;
  value: string;
  testId?: string;
}

const InfoRow = ({ icon, alt, value, testId }: InfoRowProps) => (
  <div className="flex flex-row gap-2 items-center" data-testid={testId}>
    <Image src={icon} alt={alt} className="w-4 h-4" layout="constrained" width={96} height={96} />
    <span className="text-sm font-normal text-[#202020CC]">{value}</span>
  </div>
);

interface ActionButtonsProps {
  onViewLogs?: () => void;
  program?: string
}

const ActionButtons = ({ onViewLogs, program }: ActionButtonsProps) => {
  return (
    <>
      {program === "urgent-care" ? (
        <div className="flex gap-2 items-end w-full md:w-[133px]" data-testid="action-buttons">
          <Button
            onClick={onViewLogs}
            className="bg-[#4272DD] text-white rounded-[8px] h-fit w-full"
            aria-label="View patient logs"
          >
            View details
          </Button>
        </div>
      ) : (
        <div className="flex gap-2 items-end w-full md:w-[133px]" data-testid="action-buttons">
          <Button
            onClick={onViewLogs}
            className="button-primary h-fit w-full"
            aria-label="View patient logs"
          >
            See patient log
          </Button>
        </div>
      )}
    </>
  );
};

type ConsultationsCardProps = {
  item: FormattedEncounter;
  program: string
}

const ConsultationsCard = ({
  item,
  program
}: ConsultationsCardProps) => {
  const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
  const navigate = routeApi.useNavigate();
  const search = routeApi.useSearch() as { encounter_id?: string };
  const encounter_id = search.encounter_id;
  const { title, createdAt, id } = item;

  const [isPatientLogModalOpen, setIsPatientLogModalOpen] = useState(false);

  // Check if this encounter matches the encounter_id in URL params
  useEffect(() => {
    if (encounter_id === id) {
      setIsPatientLogModalOpen(true);
    }
  }, [encounter_id, id]);

  const encounterInfo = [
    { icon: "/icons/meeting-now.svg", alt: "calendar", value: createdAt, testId: "meeting-date" },
    // {
    //   icon: "/icons/flash-on.svg",
    //   alt: "Express consultation",
    //   value: `${item.patient.medical_plan?.consultation?.name ? item.patient.medical_plan.consultation.name.charAt(0).toUpperCase() + item.patient.medical_plan.consultation.name.slice(1) : ""} consultation`,
    //   testId: `${item.patient.medical_plan?.consultation?.name ? item.patient.medical_plan.consultation.name.charAt(0).toUpperCase() + item.patient.medical_plan.consultation.name.slice(1) : ""}-consultation`
    // }
  ];

  const handleViewPatientLogs = () => {
    navigate({
      search: { ...search, encounter_id: id },
    });
  };

  const handleModalClose = (open: boolean) => {
    setIsPatientLogModalOpen(open);
    if (!open) {
      // Clear the encounter_id from URL when modal is closed
      navigate({
        search: { ...search, encounter_id: undefined },
      });
    }
  };

  const timeAgo = formatTimeAgo(item.instanceCreated);

const diseaseName =
    Array.isArray(item?.session?.diagnosis) && item.session.diagnosis.length > 0
      ? item?.session?.diagnosis[0]?.disease_name ?? "No disease name"
      : "No diagnosis available";

  // Check for medication_payment tasks to determine resolved/action needed status
  const normalizedTasks = Array.isArray(item.recommendedTasks) ? item.recommendedTasks : [];
  const medicationPaymentTasks = normalizedTasks.filter(
    (task: any) => task?.type === "medication_payment"
  );

  // There may be multiple medication_payment tasks.
  const anyCompletedPayment = medicationPaymentTasks.some(
    (task: any) => task?.status === "completed"
  );
  const anyNotCompletedPayment = medicationPaymentTasks.some(
    (task: any) => task?.status !== "completed"
  );

  // Find the first completed task for erx check
  const completedPaymentTask = medicationPaymentTasks.find(
    (task: any) => task?.status === "completed"
  );

  // showResolvedTag only if ALL are completed and is_erx_sent is true for the completed one.
  // showActionNeededTag if ANY are incomplete, OR if all are completed but is_erx_sent is NOT true for the completed one.
  const showResolvedTag = completedPaymentTask?.is_erx_sent === true && !anyNotCompletedPayment;
  const showActionNeededTag =
    (anyNotCompletedPayment && anyCompletedPayment) || // some completed, some not
    (anyCompletedPayment && !anyNotCompletedPayment && completedPaymentTask?.is_erx_sent !== true); // all completed, but is_erx_sent is NOT true
  const showInProgressTag =
    !anyCompletedPayment && anyNotCompletedPayment;
  return (
    <>
      <article
        className="flex md:flex-row flex-col gap-4 p-4 border border-[#E6E6E6] border-l-[6px] border-l-[#7B238D] shadow-sm bg-white justify-between items-center w-full"
        data-testid="consultation-card"
      >
        <div className="flex flex-col gap-3 w-full">
          <div className="flex gap-2 items-center">
            <ConsultationStatus tag="Consultation" program={program} />
            {showResolvedTag && (
              <span className="text-xs font-medium py-[4px] px-[6px] rounded-full text-[#16A34A] bg-[#ECFDF3] border border-[#BAE6C3] w-fit flex items-center gap-1">
                <CircleCheck className="size-[14px] text-[#16A34A]" />
                Resolved
              </span>
            )}
            {showActionNeededTag && (
              <span className="text-xs font-medium py-[4px] px-[6px] rounded-full text-[#FFA726] bg-[#FFF5E6] w-fit flex items-center gap-1">
                <Info className="size-[14px] text-[#FFA726]" />
                Action needed
              </span>
            )}
            {showInProgressTag && (
              <span className="text-xs font-medium py-[4px] px-[6px] rounded-full text-[#2563EB] bg-[#EFF6FF] w-fit flex items-center gap-1">
                <Clock className="size-[14px] text-[#2563EB]" />
                In progress
              </span>
            )}
          </div>
          <div className="flex flex-row gap-2 sm:gap-3 sm:items-center items-start">
            <h2
              className="text-lg sm:text-[20px] leading-normal font-semibold text-[#2D2F35] break-words"
              data-testid="consultation-title"
            >
              {title}
            </h2>
            {program === "urgent-care" ? (
              item.patient.urgent_care_onboarded && item.patient.identity_verified && (
                <div className="flex-shrink-0 mt-1 sm:mt-0">
                  <Image src="/icons/check.svg"
                    alt="timeline"
                    className="w-4 h-4 sm:w-5 sm:h-5" layout="constrained" width={96} height={96} />
                </div>
              )
            ) : (
              item.patient.medical_plan?.payment_status === "paid" &&
              item.patient.glp1_guide_accepted &&
              item.patient.identity_verified &&
              item.patient.supplementary_health_assessment &&
              Object.keys(item.patient.supplementary_health_assessment).length > 0 && (
                <div className="flex-shrink-0 mt-1 sm:mt-0">
                  <Image src="/icons/check.svg"
                    alt="timeline"
                    className="w-4 h-4 sm:w-5 sm:h-5" layout="constrained" width={96} height={96} />
                </div>
              )
            )}
          </div>

          {program === "urgent-care" ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center text-sm font-normal text-[#202020CC] gap-2 flex-wrap">
                <span>{timeAgo}</span>
                {item.patient.age !== undefined && (
                  <>
                    <span className="mx-2">•</span>
                    <span>{item.patient.age} years old</span>
                  </>
                )}
                {item.patient.gender && (
                  <>
                    <span className="mx-2">•</span>
                    <span>{item.patient.gender}</span>
                  </>
                )}
              </div>
              <p className="text-sm font-normal text-[#1A202C]">
                <span className="font-semibold">Diagnosis:</span>{" "}
                {diseaseName}
              </p>
            </div>
          ) : (
            <div className="flex gap-2 items-start">
              {encounterInfo.map((info) => (
                <InfoRow
                  key={info.testId}
                  icon={info.icon}
                  alt={info.alt}
                  value={info.value}
                  testId={info.testId}
                />
              ))}
            </div>
          )}
        </div>

        <ActionButtons
          onViewLogs={handleViewPatientLogs}
          program={program}
        />
      </article>

      {isPatientLogModalOpen && item && (
        <ConsultationModal
          open={isPatientLogModalOpen}
          onOpenChange={handleModalClose}
          encounter={item}
        />
      )}
    </>
  );
};

export default ConsultationsCard;